import { useEffect, useState } from 'react';
import * as S from './ListeningMCQLMultipleAnswer.styles';
import { CloseIcon } from '@app/components/common/icons/CloseIcon';
import { CheckSuccessIcon } from '@app/components/common/icons/CheckSuccessIcon';
import { STAGES } from '@app/constants/stages';
import { AudioPlayer } from '@app/components/exam/AudioPlayer';
import useCountdownTimer from '@app/hooks/useCountdownTimer';
import { ScoreInfo } from '@app/components/exam/ScoreInfo';

interface Props {
  audioQuestion: string;
  prepTime: number;
  showAnswer: boolean;
  initialChoicesString: string;
  questionNumber: string;
  correctAnswersString: string;
  isFirstRender: React.MutableRefObject<boolean>;
  allowDrag: boolean;
  onAnswerChange?: (answer: string) => void;
  canStart: boolean;
}

export const ListeningMCQLMultipleAnswer: React.FC<Props> = ({
  audioQuestion,
  prepTime,
  showAnswer,
  initialChoicesString,
  questionNumber,
  correctAnswersString,
  allowDrag,
  onAnswerChange,
  canStart,
}) => {
  const [stage, setStage] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState<boolean>(false);
  const [choices, setChoices] = useState<{ label: string; text: string }[]>([]);
  const [correctAnswers, setCorrectAnswers] = useState<string[]>([]);
  const [selectedAnswers, setSelectedAnswers] = useState<string[]>([]);
  const [scoreLoaded, setScoreLoaded] = useState(false);

  const { seconds, isRunning, start, reset } = useCountdownTimer({
    initialSeconds: prepTime,
    onComplete: () => {
      setStage(STAGES.PLAYING);
      setIsPlaying(true);
    },
  });

  useEffect(() => {
    // if (isFirstRender.current) {
    //   isFirstRender.current = false;
    //   showPermissionModal();
    // } else {
    //   initialize();
    // }
    initialize();
  }, [questionNumber]);

  useEffect(() => {
    onAnswerChange && onAnswerChange(selectedAnswers.join(','));
  }, [selectedAnswers]);

  // const showPermissionModal = () => {
  //   InfoModal({
  //     title: 'Additional permissions',
  //     content: 'Click the OK button to enable record audio.',
  //     onOk: () => {
  //       initialize();
  //     },
  //   });
  // };

  const initialize = () => {
    setSelectedAnswers([]);
    setStage(null);
    setIsPlaying(false);
    reset();

    if (initialChoicesString) {
      const parsedChoices = JSON.parse(initialChoicesString);
      setChoices(parsedChoices);
    }
    if (correctAnswersString) {
      const resultArray = correctAnswersString.split(',').map((item) => item.trim());
      setCorrectAnswers(resultArray);
    }

    start();
    setStage(STAGES.START);
  };

  const handleEnded = () => {
    setIsPlaying(false);
    if (stage === STAGES.PLAYING) setStage(STAGES.COMPLETE);
  };

  const handleCheckboxChange = (label: string) => {
    setSelectedAnswers((prev) => (prev.includes(label) ? prev.filter((item) => item !== label) : [...prev, label]));
  };

  return (
    <>
      <S.Wrapper>
        {/* {stage === STAGES.START && (
          <p>
            Beginning in <S.TextRed>{seconds}</S.TextRed> seconds
          </p>
        )}
        {stage === STAGES.PLAYING && <p>Status: playing...</p>}
        {stage === STAGES.COMPLETE && <p>Status: Completed</p>} */}
        <S.Box>
          <p className="font-bold mb-2">Audio Recorder</p>
          {stage === STAGES.START && <S.TextRed>Beginning in {seconds}</S.TextRed>}
          {stage === STAGES.PLAYING && <S.TextRed>Playing</S.TextRed>}
        </S.Box>
        <AudioPlayer
          audioQuestion={audioQuestion}
          onEnded={handleEnded}
          startPlaying={isPlaying}
          waitingTime={isRunning}
          allowDrag={allowDrag}
          style={{ background: '#F1F1F1', padding: '0.7rem 0.5rem' }}
          canStart={canStart}
        />
      </S.Wrapper>

      <S.ContentQuestion>Which of the following best summarises the main message of this text?</S.ContentQuestion>

      {choices.map((item) => (
        <div key={item.label} className="flex gap-2 items-center mb-4">
          {showAnswer && scoreLoaded && (
            <span
              style={{
                width: '1em',
                fontSize: '1rem',
                color: correctAnswers.includes(item.label) ? 'green' : 'red',
              }}
            >
              {correctAnswers.includes(item.label) ? <CheckSuccessIcon /> : <CloseIcon />}
            </span>
          )}
          <S.CheckboxStyle
            checked={selectedAnswers.includes(item.label)}
            onChange={() => handleCheckboxChange(item.label)}
          >
            {item.text}
          </S.CheckboxStyle>
        </div>
      ))}

      {showAnswer && (
        <ScoreInfo
          answer={selectedAnswers.join(',') || ''}
          questionId={questionNumber}
          onLoaded={() => setScoreLoaded(true)}
        />
      )}
    </>
  );
};
